import type { ReactElement, ReactNode, SyntheticEvent } from 'react';
import { useCallback, useMemo } from 'react';

import type { OperationVariables, TypedDocumentNode } from '@apollo/client';
import type {
    AutocompleteChangeDetails,
    AutocompleteChangeReason,
    AutocompleteProps,
    AutocompleteValue,
} from '@mui/material/Autocomplete';
import type { TextFieldProps } from '@mui/material/TextField';

import { useQuery } from '@apollo/client/react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

import { generateOptionsArray, generatePlaceholder } from '../../../utils/inputHelpers';

interface GenericFetchAutocompletePropsWrapperType<T> {
    value: Array<T>;
    handleOnBlur?: () => void;
    handleOnChange: (event: SyntheticEvent, value: Array<T> | T | null) => void;
    handleOnFocus?: () => void;
    textFieldProps?: TextFieldProps;
    limitTags?: number;
    size?: 'medium' | 'small';
    optionsFilterFn?: (option: T) => boolean;
    disabled?: boolean;
}

interface GenericFetchAutocompleteProps<
    D,
    TData,
    TVariables extends OperationVariables,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
> extends Omit<
        AutocompleteProps<
            // CollectionOption: The type for the options and returned value(s)
            D,
            // Multiple: Can be set by the consuming Frontend
            Multiple,
            // DisableClearable: Can be set by the consuming Frontend
            DisableClearable,
            // FreeSolo: Must be false, otherwise value can be of type string
            false
        >,
        'freeSolo' | 'options' | 'renderInput'
    > {
    fetchQuery: TypedDocumentNode<TData, TVariables>;
    textFieldProps?: TextFieldProps;
    queryOptions: useQuery.Options<NoInfer<TData>, NoInfer<TVariables>>;
    sortingKey?: keyof D;
    idKey: keyof D;
    dataMapper: (data: TData) => Array<D>;
    optionLabelFormatter: (option: D) => string;
    tagLabelFormatter: (tag: D) => string;
    optionsFilterFn?: (option: D) => boolean;
    inputLabels: {
        single: string;
        plural: string;
    };
}

/**
 * GenericFetchAutocomplete Component
 *
 * A flexible Autocomplete component that allows you to fetch and display options in an input field.
 *
 * @template D - The type for the options and returned value(s)
 * @template TData - The type for data fetched from the query
 * @template TVariables - Variables for the query
 * @param {DocumentNode | TypedDocumentNode<TData>} fetchQuery - The GraphQL query document to fetch options.
 * @param {TextFieldProps} textFieldProps - Additional props for the text field.
 * @param {ReactNode} value - The current value of the Autocomplete component.
 * @param {boolean} multiple - Indicates if multiple selections are allowed.
 * @param {boolean} limitTags - Indicates the maximum number of tags to display.
 * @param {keyof D | undefined} sortingKey - Key for sorting the options.
 * @param {useQuery.Options<TData, TVariables>} queryOptions - Options for the query.
 * @param {keyof D} idKey - Key to identify options.
 * @param {{ single: string, plural: string }} inputLabels - Labels for the input field.
 * @param {(data: TData) => Array<D>} dataMapper - Function to map fetched data to options.
 * @param {(option: D) => string} optionLabelFormatter - Function to format option labels.
 * @param {(option: D) => string} tagLabelFormatter - Function to format tag labels.
 * @param {AutocompleteProps} autoCompleteProps - Additional props for the Autocomplete component.
 *
 * @returns {ReactElement} - The rendered GenericFetchAutocomplete component.
 */
const GenericFetchAutocomplete = <
    D,
    TData,
    TVariables extends OperationVariables,
    Multiple extends boolean,
    DisableClearable extends boolean = false,
>({
    fetchQuery,
    textFieldProps = {},
    value,
    multiple,
    sortingKey,
    queryOptions,
    idKey,
    inputLabels,
    dataMapper,
    onChange,
    optionLabelFormatter,
    tagLabelFormatter,
    optionsFilterFn = () => true,
    ...autoCompleteProps
}: GenericFetchAutocompleteProps<
    D,
    TData,
    TVariables,
    Multiple,
    DisableClearable
>): ReactElement => {
    const { data, loading } = useQuery<TData, TVariables>(
        fetchQuery,
        queryOptions
    );

    const options: Array<D> = useMemo(() => {
        if (!data) return [];
        const mappedOptions = dataMapper(data as TData);
        const filteredOptions = mappedOptions.filter(optionsFilterFn);
        return generateOptionsArray(undefined, filteredOptions, sortingKey);
    }, [data, dataMapper, optionsFilterFn, sortingKey]);

    const handleChangeCallback = useCallback(
        (
            event: SyntheticEvent,
            v: AutocompleteValue<D, Multiple, DisableClearable, false>,
            reason: AutocompleteChangeReason,
            details?: AutocompleteChangeDetails<D>
        ): void => {
            onChange?.(event, v, reason, details);
        },
        [onChange]
    );

    return (
        <Autocomplete
            value={value}
            loading={loading}
            options={options}
            getOptionLabel={optionLabelFormatter}
            renderInput={(params): ReactNode => (
                <TextField
                    {...params}
                    label={multiple ? inputLabels.plural : inputLabels.single}
                    placeholder={
                        params.slotProps.htmlInput['aria-expanded']
                            ? ''
                            : generatePlaceholder(value)
                    }
                    {...textFieldProps}
                    slotProps={{
                        ...params.slotProps,
                        ...textFieldProps.slotProps,
                        input: {
                            ...params.slotProps.input,
                            ...(textFieldProps.slotProps?.input as object),
                        },
                        htmlInput: {
                            ...params.slotProps.htmlInput,
                            ...(textFieldProps.slotProps?.htmlInput as object),
                        },
                    }}
                />
            )}
            isOptionEqualToValue={(o, v): boolean => {
                if (!value) return false;
                return o[idKey] === v[idKey];
            }}
            freeSolo={false}
            multiple={multiple}
            disableCloseOnSelect={multiple}
            blurOnSelect={!multiple}
            openOnFocus
            fullWidth
            onChange={handleChangeCallback}
            {...autoCompleteProps}
            slotProps={{
                chip: { size: 'small' },
            }}
        />
    );
};

export { GenericFetchAutocomplete };
export type { GenericFetchAutocompleteProps, GenericFetchAutocompletePropsWrapperType };
